/**
 * Pomodoro Service Module
 *
 * Handles pomodoro and break session operations.
 * All functions use atomic database operations for consistency.
 */

import { supabase } from '../supabase';

/**
 * Completed pomodoro record type
 */
export interface CompletedPomodoro {
  id: string;
  user_id: string;
  duration_minutes: number;
  xp_earned: number;
  critical_success: boolean;
  task_name: string | null;
  notes: string | null;
  completed_at: string;
}

/**
 * Save completed pomodoro atomically
 * Uses atomic database function to prevent inconsistent state
 */
export async function saveCompletedPomodoro(
  userId: string,
  data: {
    duration_minutes: number;
    xp_earned: number;
    critical_success?: boolean;
    task_name?: string;
    notes?: string;
  }
): Promise<string | null> {
  if (import.meta.env.DEV) console.log(`[User Sync] Saving pomodoro for user ${userId}`);

  // Ensure we have a valid session before calling RPC
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session) {
    if (import.meta.env.DEV) console.warn('[User Sync] No active session, attempting refresh...');
    // Attempt one refresh
    const {
      data: { session: refreshedSession },
      error: refreshError,
    } = await supabase.auth.refreshSession();

    if (refreshError || !refreshedSession) {
      if (import.meta.env.DEV)
        console.warn('[User Sync] Session refresh failed, returning null for queue handling');
      return null;
    }
  }

  // Use atomic RPC function to save pomodoro and update stats in one transaction
  // RPC validates auth.uid() matches user's auth_user_id
  const { data: pomodoroId, error } = await supabase.rpc('atomic_save_completed_pomodoro', {
    p_user_id: userId,
    p_duration_minutes: data.duration_minutes,
    p_xp_earned: data.xp_earned,
    p_critical_success: data.critical_success || false,
    p_task_name: data.task_name || null,
    p_notes: data.notes || null,
  });

  if (error) {
    console.error('[User Sync] Error saving pomodoro:', error);
    throw new Error(`Failed to save pomodoro: ${error.message}`);
  }

  if (import.meta.env.DEV) console.log('[User Sync] Pomodoro saved successfully:', pomodoroId);
  return pomodoroId as string;
}

/**
 * Get user's recent pomodoros
 */
export async function getRecentPomodoros(
  userId: string,
  limit: number = 10
): Promise<CompletedPomodoro[]> {
  const { data, error } = await supabase
    .from('completed_pomodoros')
    .select('*')
    .eq('user_id', userId)
    .order('completed_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('[User Sync] Error fetching pomodoros:', error);
    throw new Error('Failed to fetch pomodoro history');
  }

  return (data as CompletedPomodoro[]) || [];
}

/**
 * Save completed break atomically with dual authentication support
 * Uses atomic database function to prevent inconsistent state
 */
export async function saveCompletedBreak(
  userId: string,
  data: {
    break_type: 'short' | 'long';
    duration_minutes: number;
    xp_earned: number;
  }
): Promise<string | null> {
  if (import.meta.env.DEV) console.log(`[User Sync] Saving break for user ${userId}`);

  // Ensure we have a valid session before calling RPC
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session) {
    if (import.meta.env.DEV)
      console.warn('[User Sync] No active session for break save, attempting refresh...');
    const {
      data: { session: refreshedSession },
      error: refreshError,
    } = await supabase.auth.refreshSession();

    if (refreshError || !refreshedSession) {
      if (import.meta.env.DEV)
        console.warn(
          '[User Sync] Session refresh failed for break, returning null for queue handling'
        );
      return null;
    }
  }

  // RPC calls automatically include the auth token if a session exists
  // We rely on RLS/RPC security definer to handle authentication validation

  const { data: breakId, error } = await supabase.rpc('atomic_save_completed_break', {
    p_user_id: userId,
    p_break_type: data.break_type,
    p_duration_minutes: data.duration_minutes,
    p_xp_earned: data.xp_earned,
  });

  if (error) {
    console.error('[User Sync] Error saving break:', error);
    throw new Error(`Failed to save break: ${error.message}`);
  }

  if (import.meta.env.DEV) console.log('[User Sync] Break saved successfully:', breakId);
  return breakId as string;
}

/**
 * Get user statistics
 */
export async function getUserStats(userId: string): Promise<{
  totalPomodoros: number;
  totalMinutes: number;
  averagePerDay: number;
  currentStreak: number;
}> {
  const { data: user, error } = await supabase
    .from('users')
    .select('total_pomodoros, total_study_minutes, consecutive_login_days, total_unique_days')
    .eq('id', userId)
    .single();

  if (error) {
    console.error('[User Sync] Error fetching stats:', error);
    throw new Error('Failed to fetch statistics');
  }

  // Calculate average based on total unique days (not consecutive days)
  const averagePerDay =
    user.total_pomodoros > 0
      ? Math.round(user.total_pomodoros / Math.max(1, user.total_unique_days))
      : 0;

  return {
    totalPomodoros: user.total_pomodoros,
    totalMinutes: user.total_study_minutes,
    averagePerDay,
    currentStreak: user.consecutive_login_days,
  };
}
